import {
  Header,
  Button,
  Space,
  Text,
  DropDown,
  Input,
  Card,
} from "../../Components/Index";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { getUserProfile, updateUserProfile } from "../../services/api/userApi";
import { getMyMechanicProfile, deleteMyMechanicProfile } from "../../services/api/mechanicApi";
import { useState, useEffect, useRef } from "react";
import { extractErrorMessage } from "../../services/api/errorHandler";
import {
  DefaultAvatarIcon,
  WrenchIcon,
  ChatIcon,
  SupportIcon,
  HomeIcon,
  EditIcon,
  CameraIcon,
  TrashIcon,
  LogoutIcon,
} from "./UserPanelIcons";

// No profile-image endpoint exists on the backend (see user.routes.js — only
// GET/PUT/DELETE /users/profile, and the profile payload carries no image
// field). To preserve backend contracts and backward compatibility, the
// avatar is stored client-side per user and layered over any image field the
// API may return in the future.
const AVATAR_STORAGE_PREFIX = "tasmeh:user-avatar:";
const AVATAR_MAX_SIZE = 2 * 1024 * 1024; // 2MB
const AVATAR_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

function getAvatarKey(profile) {
  const id = profile?.id ?? profile?.phone;
  return id !== undefined && id !== null ? `${AVATAR_STORAGE_PREFIX}${id}` : null;
}

function UserPanel() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Service-worker registration status, resolved from the REAL backend
  // (GET /mechanics/me). "checking" while the request is in flight,
  // "registered" when the backend returns a mechanic profile, and
  // "unregistered" on 404 (not registered yet) or any request failure.
  const [mechanicStatus, setMechanicStatus] = useState("checking");

  // ویرایش اطلاعات / Edit user information (existing updateUserProfile API)
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "" });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Service-worker account deletion (real DELETE /mechanics/me).
  // Modal-gated, loading-guarded, main account stays authenticated.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // تصویر پروفایل / Profile image (client-side, see note above)
  const [avatar, setAvatar] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef(null);

  // UserPanel is shared by both navigation sections. The router preserves
  // the section that led here so the switch button points back to the other
  // dashboard instead of always showing the service-worker entry.
  const isServiceWorkerContext = (() => {
    if (pathname !== "/UserPanel") return false;
    try {
      return window.sessionStorage.getItem("tasmeh:lastSection") === "serviceWorker";
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await getUserProfile();
        if (response.success && response.data) {
          setProfile(response.data);
        } else {
          setError(response.message || "خطا در دریافت اطلاعات پروفایل");
        }
      } catch (err) {
        setError(extractErrorMessage(err, "خطا در دریافت اطلاعات پروفایل"));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  // Resolve the service-worker registration status from the REAL backend.
  // GET /mechanics/me returns the mechanic profile when the user already
  // registered, and 404 when they have not — no hardcoded or fake state.
  useEffect(() => {
    let cancelled = false;
    const checkMechanicStatus = async () => {
      if (!user) {
        setMechanicStatus("unregistered");
        return;
      }
      try {
        const response = await getMyMechanicProfile();
        if (!cancelled) {
          setMechanicStatus(response?.success && response?.data ? "registered" : "unregistered");
        }
      } catch {
        // 404 = not registered yet (the normal case); any other failure
        // also falls back to the registration entry point.
        if (!cancelled) setMechanicStatus("unregistered");
      }
    };

    checkMechanicStatus();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Persist mechanic status so AppRouter can synchronously decide the bottom
  // bar on shared routes (/UserPanel, /LatestServices) without waiting for
  // its own async fetch — covers direct navigation + refresh for workers.
  useEffect(() => {
    if (mechanicStatus === "checking") return;
    const isMech = mechanicStatus === "registered";
    try {
      window.localStorage.setItem("tasmeh:isMechanic", isMech ? "true" : "false");
      window.sessionStorage.setItem("tasmeh:isMechanic", isMech ? "true" : "false");
      // Warm lastSection for workers on direct navigation ONLY when there is
      // no in-session hint yet. Never overwrite the section the user is
      // currently using: a registered worker who navigated here from the
      // customer section must keep the customer bar on /UserPanel.
      if (isMech && !window.sessionStorage.getItem("tasmeh:lastSection")) {
        window.localStorage.setItem("tasmeh:lastSection", "serviceWorker");
        window.sessionStorage.setItem("tasmeh:lastSection", "serviceWorker");
      }
      try {
        window.dispatchEvent(new Event("tasmeh:nav-changed"));
      } catch {}
    } catch {}
  }, [mechanicStatus]);

  // Load the stored avatar whenever the active profile changes.
  useEffect(() => {
    setAvatarError("");
    const key = getAvatarKey(profile);
    if (!key) {
      setAvatar(null);
      return;
    }
    try {
      setAvatar(window.localStorage.getItem(key));
    } catch {
      setAvatar(null);
    }
  }, [profile]);

  // Single reliable logout handler for every visible `خروج از حساب`
  // button on this page. Order matters: clear ALL auth state first
  // (localStorage/sessionStorage token+user via AuthContext.logout, plus
  // in-memory token/user so ProtectedRoute flips to unauthenticated),
  // then replace-navigate to the login route so back-button cannot land
  // back on the panel while authenticated. No backend logout endpoint
  // exists (see auth.routes.js — only send-otp/verify-otp), so this is a
  // complete client-side logout per the project architecture.
  const handleLogout = async () => {
    if (logoutLoading) return;
    setLogoutLoading(true);
    setError("");
    try {
      // These states are account-specific. Notify mounted providers before
      // authentication changes so the old vehicle cannot flash on next login.
      window.localStorage.removeItem("selectedCarId");
      window.localStorage.removeItem("selectedVehicle");
      window.dispatchEvent(new Event("tasmeh:account-logout"));
      logout();
      try {
        window.localStorage.removeItem("tasmeh:isMechanic");
        window.sessionStorage.removeItem("tasmeh:isMechanic");
        window.localStorage.removeItem("tasmeh:lastSection");
        window.sessionStorage.removeItem("tasmeh:lastSection");
        try {
          window.dispatchEvent(new Event("tasmeh:nav-changed"));
        } catch {}
      } catch {}
      navigate("/LoginNumber", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err, "خطا در خروج از حساب کاربری"));
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleStartEdit = () => {
    setForm({
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
    });
    setSaveError("");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError("");
    setForm({
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
    });
  };

  const handleSaveEdit = async () => {
    setSaveLoading(true);
    setSaveError("");
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      };
      const response = await updateUserProfile(payload);
      if (response.success && response.data) {
        setProfile((prev) => ({ ...prev, ...response.data }));
      } else {
        setProfile((prev) => ({ ...prev, ...payload }));
      }
      setIsEditing(false);
    } catch (err) {
      setSaveError(extractErrorMessage(err, "خطا در ذخیره اطلاعات کاربری"));
    } finally {
      setSaveLoading(false);
    }
  };

  const openFilePicker = () => {
    setAvatarError("");
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";
    if (!file) return;

    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      setAvatarError("فرمت تصویر معتبر نیست. فقط JPG ،PNG ،WebP یا GIF انتخاب کنید.");
      return;
    }
    if (file.size > AVATAR_MAX_SIZE) {
      setAvatarError("حجم تصویر باید کمتر از ۲ مگابایت باشد.");
      return;
    }

    setAvatarLoading(true);
    setAvatarError("");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setAvatar(dataUrl);
      setAvatarLoading(false);
      const key = getAvatarKey(profile);
      if (key) {
        try {
          window.localStorage.setItem(key, dataUrl);
        } catch {
          setAvatarError("ذخیره تصویر انجام نشد. حافظه مرورگر کافی نیست.");
        }
      }
    };
    reader.onerror = () => {
      setAvatarLoading(false);
      setAvatarError("خطا در خواندن فایل تصویر. دوباره تلاش کنید.");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatar(null);
    setAvatarError("");
    const key = getAvatarKey(profile);
    if (key) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Removing is best-effort; the UI already returned to the default icon.
      }
    }
  };

  const handleOpenDeleteConfirm = () => {
    setDeleteError("");
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    // Keep the modal open while the request is in flight so the user
    // cannot dismiss mid-flight and fire a duplicate request.
    if (deleteLoading) return;
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  // Delete ONLY the service-worker (mechanic) profile via the real backend
  // endpoint DELETE /mechanics/me (permanent mechanic-profile deletion). Guarded against
  // duplicate clicks. On success the service-worker state flips to
  // "unregistered" immediately so `Register as Service Worker` shows again;
  // the main user account stays authenticated (no logout, profile kept).
  // On failure the error surfaces in the modal and the current
  // (registered) state is left unchanged.
  const handleConfirmDelete = async () => {
    if (deleteLoading) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deleteMyMechanicProfile();
      setMechanicStatus("unregistered");
      try {
        window.localStorage.setItem("tasmeh:isMechanic", "false");
        window.sessionStorage.setItem("tasmeh:isMechanic", "false");
        window.localStorage.setItem("tasmeh:lastSection", "customer");
        window.sessionStorage.setItem("tasmeh:lastSection", "customer");
        try {
          window.dispatchEvent(new Event("tasmeh:nav-changed"));
        } catch {}
      } catch {}
      setShowDeleteConfirm(false);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "خطا در حذف حساب سرویس کار"));
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header Text="پنل کاربری" hideR="" LinkR={-1} />
        <div className="user-panel">
          <Space size="xl" />
          <Text position="center" FontSize="lg">
            در حال بارگذاری پروفایل...
          </Text>
        </div>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        <Header Text="پنل کاربری" hideR="" LinkR={-1} />
        <div className="user-panel">
          <Space size="xl" />
          <Text position="center" FontSize="lg" color="red">
            {error || "اطلاعات کاربری یافت نشد"}
          </Text>
          <Space />
          <Button color="error" onClick={handleLogout} disabled={logoutLoading}>
            <span className="user-panel__logout">
              <LogoutIcon size={18} />
              {logoutLoading ? "در حال خروج..." : "خروج از حساب"}
            </span>
          </Button>
          <Space size="xl" />
        </div>
      </>
    );
  }

  const fullName =
    `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "کاربر";
  const displayAvatar = avatar || profile.profileImage || profile.avatar || profile.image || null;

  return (
    <>
      <Header Text="پنل کاربری" hideR="" LinkR={-1} />
      <main className="user-panel">
        <Card size="full" className="user-panel__profile">
          <div className="user-panel__identity">
            <div className="user-panel__avatar-block">
              <button
                type="button"
                className="user-panel__avatar"
                onClick={openFilePicker}
                disabled={avatarLoading}
                aria-label={displayAvatar ? "تغییر تصویر پروفایل" : "افزودن تصویر پروفایل"}
              >
                {displayAvatar ? (
                  <img src={displayAvatar} alt={fullName} />
                ) : (
                  <span className="user-panel__avatar-default">
                    <DefaultAvatarIcon />
                  </span>
                )}
                {avatarLoading && (
                  <span className="user-panel__avatar-spinner" aria-hidden="true" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={AVATAR_ACCEPTED_TYPES.join(",")}
                onChange={handleFileSelect}
                hidden
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>

            <div className="user-panel__who">
              <h2 className="user-panel__name">{fullName}</h2>
              <p className="user-panel__meta">{profile.phone || "نامشخص"}</p>
              <p className="user-panel__meta user-panel__meta--dim">
                شناسه: {profile.id?.toString() || "نامشخص"}
              </p>
            </div>

            <button
              type="button"
              className="user-panel__edit"
              onClick={isEditing ? handleCancelEdit : handleStartEdit}
            >
              <EditIcon size={18} />
              ویرایش اطلاعات
            </button>
          </div>

          {avatarError && (
            <p className="user-panel__error" role="alert">
              {avatarError}
            </p>
          )}

          <div className="user-panel__avatar-actions">
            <button
              type="button"
              className="user-panel__avatar-btn"
              onClick={openFilePicker}
              disabled={avatarLoading}
            >
              <CameraIcon size={18} />
              {avatarLoading
                ? "در حال بارگذاری..."
                : displayAvatar
                  ? "تغییر تصویر"
                  : "افزودن تصویر پروفایل"}
            </button>
            {displayAvatar && (
              <button
                type="button"
                className="user-panel__avatar-btn user-panel__avatar-btn--danger"
                onClick={handleRemoveAvatar}
                disabled={avatarLoading}
              >
                <TrashIcon size={18} />
                حذف تصویر
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="user-panel__form">
              <Input
                label="نام"
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                maxLength={50}
              />
              <Space />
              <Input
                label="نام خانوادگی"
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                maxLength={50}
              />
              <Space />
              <Input label="شماره موبایل" value={profile.phone || ""} disabled />
              {saveError && (
                <p className="user-panel__error" role="alert">
                  {saveError}
                </p>
              )}
              <div className="user-panel__form-actions">
                <Button color="dark" size="fif" onClick={handleSaveEdit} disabled={saveLoading}>
                  {saveLoading ? "در حال ذخیره..." : "ذخیره تغییرات"}
                </Button>
                <Button color="light" size="fif" onClick={handleCancelEdit} disabled={saveLoading}>
                  انصراف
                </Button>
              </div>
            </div>
          ) : (
            <dl className="user-panel__facts">
              <div className="user-panel__fact">
                <dt>نام</dt>
                <dd>{profile.firstName || "—"}</dd>
              </div>
              <div className="user-panel__fact">
                <dt>نام خانوادگی</dt>
                <dd>{profile.lastName || "—"}</dd>
              </div>
              <div className="user-panel__fact">
                <dt>شماره موبایل</dt>
                <dd>{profile.phone || "—"}</dd>
              </div>
            </dl>
          )}
        </Card>

        <DropDown title="نظرات" icon={<ChatIcon />}>
          <Input label="پیشنهاد ها" />
        </DropDown>

        <DropDown title="پشتیبانی" icon={<SupportIcon />}>
          <div className="user-panel__support">
            <p className="user-panel__support-description">
              توضیحات و راهنمایی موردنیاز خود را از طریق پشتیبانی دریافت کنید.
            </p>
            <a
              className="user-panel__support-call"
              href="tel:02191092058"
              aria-label="تماس با پشتیبانی هوبر"
            >
              <span>تماس با پشتیبانی</span>
              <strong dir="ltr">021-91092058</strong>
            </a>
            <small className="user-panel__support-name">هوبر، شرکت پشتیبانی</small>
          </div>
        </DropDown>

        <div className="user-panel__actions">
          {isServiceWorkerContext ? (
            <Link to="/" className="user-panel__link">
              <Button color="light">
                <span className="user-panel__action">
                  <HomeIcon size={18} />
                  ورود به داشبورد کاربری
                </span>
              </Button>
            </Link>
          ) : mechanicStatus === "registered" ? (
            <Link to="/ServiceDashbord" className="user-panel__link">
              <Button color="light">
                <span className="user-panel__action">
                  <WrenchIcon size={18} />
                  ورود به داشبورد سرویس کار
                </span>
              </Button>
            </Link>
          ) : (
            <Link to="/ServiceWorkerRegister" className="user-panel__link">
              <Button color="light" disabled={mechanicStatus === "checking"}>
                <span className="user-panel__action">
                  <WrenchIcon size={18} />
                  {mechanicStatus === "checking" ? "در حال بررسی..." : "ثبت نام به عنوان سرویس کار"}
                </span>
              </Button>
            </Link>
          )}

          {mechanicStatus === "registered" ? (
            <div className="user-panel__danger-actions">
              <div className="user-panel__link">
                <Button color="error" onClick={handleOpenDeleteConfirm} disabled={deleteLoading}>
                  <span className="user-panel__action">
                    <TrashIcon size={18} />
                    حذف سرویس‌کار
                  </span>
                </Button>
              </div>
              <div className="user-panel__link">
                <Button color="error" onClick={handleLogout} disabled={logoutLoading}>
                  <span className="user-panel__logout">
                    <LogoutIcon size={18} />
                    {logoutLoading ? "در حال خروج..." : "خروج از حساب"}
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <div className="user-panel__link">
              <Button color="error" onClick={handleLogout} disabled={logoutLoading}>
                <span className="user-panel__logout">
                  <LogoutIcon size={18} />
                  {logoutLoading ? "در حال خروج..." : "خروج از حساب"}
                </span>
              </Button>
            </div>
          )}
        </div>
        <Space size="xl" />
      </main>

      {showDeleteConfirm && (
        <div
          className="user-panel__modal-overlay"
          role="presentation"
          onClick={handleCloseDeleteConfirm}
        >
          <div
            className="user-panel__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sw-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-panel__modal-icon" aria-hidden="true">
              <TrashIcon size={22} />
            </div>
            <h3 id="sw-delete-title" className="user-panel__modal-title">
              حذف حساب سرویس کار
            </h3>
            <p className="user-panel__modal-text">
              آیا از حذف حساب سرویس کار خود مطمئن هستید؟ این عمل قابل بازگشت نیست.
            </p>
            {deleteError && (
              <p className="user-panel__error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="user-panel__modal-actions">
              <Button color="error" size="fif" onClick={handleConfirmDelete} disabled={deleteLoading}>
                {deleteLoading ? "در حال حذف..." : "حذف حساب"}
              </Button>
              <Button color="light" size="fif" onClick={handleCloseDeleteConfirm} disabled={deleteLoading}>
                انصراف
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default UserPanel;
